import { forwardRef } from 'react';

import { Icon, Icons } from 'Icon';
import { IconButton } from 'IconButton';

import { StyledAlert } from './Alert.styled';
import { AlertProps } from './types';

const alertSeverities = {
  error: true,
  info: true,
  success: true,
  warning: true,
};

const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ closeText, fullWidth = false, onClose, severity = 'success', ...props }: AlertProps, ref) => {
    /*
      The MUI Alert actually crashes if an invalid severity is passed -- this was learned the hard way when the team
      in VS-UI tried to migrate their code based on the old `next-ui` Alert over to this one. 
      The old Alert used a severity of `warn`, whereas MUI expects `warning`.
      This `if` condition has been added to prevent the problem in the future.
    */
    if (!alertSeverities[severity]) {
      console.error(`You passed an invalid \`severity\` prop of "${severity}" to an \`Alert\` component. Valid values are: ${Object.keys(
        alertSeverities,
      ).join(', ')}. 

To avoid crashing your application, next-ui-components has set the severity to \`success\` instead.
Please review your code and pass a valid severity to get rid of this error message.`);

      severity = 'success';
    }

    return (
      <StyledAlert
        ref={ref}
        {...props}
        action={
          onClose ? (
            <IconButton aria-label={closeText || 'close alert message'} onClick={onClose}>
              <Icon icon="close" size={16} />
            </IconButton>
          ) : undefined
        }
        fullWidth={fullWidth}
        iconMapping={{
          info: <Icon icon={Icons.info} size={16} />,
          error: <Icon icon={Icons.remove} size={16} />,
          success: <Icon icon={Icons.checkCircle} size={16} />,
          warning: <Icon icon={Icons.exclamation} size={16} />,
        }}
        severity={severity}
      />
    );
  },
);
Alert.displayName = 'Alert';

export { Alert };
